import styled from "@emotion/styled";

import type {
  FontSizeProps,
  SpaceProps,
  FontWeightProps,
  LineHeightProps,
  ThemeValue,
} from "styled-system";
import {
  space,
  fontSize,
  fontWeight,
  lineHeight,
  variant,
} from "styled-system";

import type { ExactTheme } from "src/lib/emotion/theme.ts";
import { type ForwardedRef, forwardRef, type PropsWithChildren } from "react";
import { Skeleton } from "src/components/Skeleton/Skeleton.tsx";

interface VariantProps {
  variant?: ThemeValue<"textV2", ExactTheme>;
  skin?: "default";
}

type Theme = Omit<ExactTheme, "buttons">;

interface TextV2StyledSystemProps
  extends SpaceProps<Theme>,
    FontSizeProps<Theme>,
    FontWeightProps<Theme>,
    LineHeightProps<Theme>,
    VariantProps {
  isLoading?: boolean;
}

export type TextV2Props = PropsWithChildren<TextV2StyledSystemProps>;

const textV2Theme = (theme: ExactTheme) => ({
  default: {
    color: theme.colorsV2.fg.default,
  },
});

export const StyledTextV2 = styled("span", {
  shouldForwardProp: (prop) => !["color", "skin"].includes(prop),
})<TextV2Props>`
  ${variant({
    scale: "textV2",
  })}
  ${({ theme }) =>
    variant({
      prop: "skin",
      variants: textV2Theme(theme),
    })}
    ${space}
    ${fontSize}
    ${fontWeight}
    ${lineHeight}
`;

const TextV2Impl = ((
  {
    isLoading = false,
    variant = "body2",
    skin = "default",
    ...rest
  }: TextV2Props,
  ref: ForwardedRef<HTMLSpanElement>,
) => {
  return (
    <Skeleton isLoading={isLoading}>
      <StyledTextV2 variant={variant} skin={skin} {...rest} ref={ref} />
    </Skeleton>
  );
}) as typeof StyledTextV2;

export const TextV2 = forwardRef(
  // @ts-ignore
  TextV2Impl,
) as unknown as typeof TextV2Impl;
